import { defineStore } from 'pinia';
import DocNetInboxService from 'src/services/docnet/DocNetInboxService';
import { useDocNetIssueStore } from './docnet-issues.store';
import { useDocNetMissionStore } from './docnet-missions.store';
import { useDocNetSettingsStore } from './docnet-settings.store';
import type {
  DocNetInboxItem,
  DocNetInboxItemType,
  DocNetInboxItemStatus,
} from 'src/@types/docnet';
import { mockInboxItems } from 'src/mocks/docnet/inbox';

const inboxService = new DocNetInboxService();

export interface DocNetInboxFilters {
  type: DocNetInboxItemType | '';
  status: DocNetInboxItemStatus | '';
}

export const useDocNetInboxStore = defineStore('docnet_inbox', {
  state: () => ({
    items: [] as DocNetInboxItem[],
    currentItem: null as DocNetInboxItem | null,
    loading: false,
    filters: {
      type: '',
      status: '',
    } as DocNetInboxFilters,
    selectedIds: new Set<string>(),
    bulkActing: false,
  }),

  getters: {
    /**
     * Items whose linked mission is NOT completed or cancelled.
     * Items without a mission_id are always included.
     */
    activeItems(state): DocNetInboxItem[] {
      const missionStore = useDocNetMissionStore();
      const completedMissionIds = new Set(
        missionStore.missions
          .filter((m) => m.status === 'completed' || m.status === 'cancelled')
          .map((m) => m.id),
      );
      return state.items.filter(
        (item) => !item.mission_id || !completedMissionIds.has(item.mission_id),
      );
    },

    pendingItems(): DocNetInboxItem[] {
      return this.activeItems.filter((item: DocNetInboxItem) => item.status === 'pending');
    },

    pendingCount(): number {
      return this.pendingItems.length;
    },

    recentlyResolvedItems(): DocNetInboxItem[] {
      return this.activeItems
        .filter((item: DocNetInboxItem) => item.status === 'resolved' || item.status === 'dismissed')
        .sort((a: DocNetInboxItem, b: DocNetInboxItem) => new Date(b.acted_at || b.created_on).getTime() - new Date(a.acted_at || a.created_on).getTime())
        .slice(0, 5);
    },

    filteredItems(state): DocNetInboxItem[] {
      return this.activeItems.filter((item: DocNetInboxItem) => {
        if (state.filters.type && item.type !== state.filters.type) {
          return false;
        }
        // When no status filter is set, only show pending items
        if (!state.filters.status && item.status !== 'pending') {
          return false;
        }
        if (state.filters.status && item.status !== state.filters.status) {
          return false;
        }
        return true;
      });
    },

    selectedCount(state): number {
      return state.selectedIds.size;
    },

    allFilteredSelected(): boolean {
      const pending = this.filteredItems.filter((i: DocNetInboxItem) => i.status === 'pending');
      if (pending.length === 0) return false;
      return pending.every((i: DocNetInboxItem) => this.selectedIds.has(i.id));
    },

    selectedItems(state): DocNetInboxItem[] {
      return state.items.filter((i) => state.selectedIds.has(i.id));
    },
  },

  actions: {
    async fetchItem(id: string): Promise<void> {
      const settings = useDocNetSettingsStore();
      if (settings.useMockData) {
        this.currentItem = mockInboxItems.find(i => i.id === id) ?? null;
        return;
      }
      this.loading = true;
      try {
        this.currentItem = await inboxService.getItem(id);
      } catch (error) {
        console.error('[DocNet] Failed to fetch inbox item:', error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async fetchItems(): Promise<void> {
      const settings = useDocNetSettingsStore();
      if (settings.useMockData) { this.items = mockInboxItems; return; }
      this.loading = true;
      try {
        const items = await inboxService.getItems();
        this.items = items;
      } catch (error) {
        console.error('[DocNet] Failed to fetch inbox items:', error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async actOnItem(
      id: string,
      action: 'approve' | 'reject' | 'reassign' | 'comment',
      payload?: Record<string, unknown>,
    ): Promise<void> {
      const index = this.items.findIndex((i) => i.id === id);

      // If item isn't in the items array (e.g. navigated directly to detail),
      // still call the API — just skip optimistic updates.
      const hasLocalItem = index !== -1;
      const snapshot = hasLocalItem ? { ...this.items[index] } : null;

      // Optimistic update — only when we have the item locally
      if (hasLocalItem) {
        if (action === 'approve' || action === 'reject') {
          this.items[index] = {
            ...this.items[index],
            status: 'resolved',
            acted_at: new Date().toISOString(),
            resolution_notes: (payload?.resolution_notes as string) || this.items[index].resolution_notes,
          };
        } else {
          // comment/reassign: update notes only, keep status pending
          this.items[index] = {
            ...this.items[index],
            resolution_notes: (payload?.resolution_notes as string) || this.items[index].resolution_notes,
          };
        }
      }

      try {
        const updated = await inboxService.actOnItem(id, action, payload);

        // Update currentItem if it matches
        if (this.currentItem?.id === id) {
          this.currentItem = updated;
        }

        // Refresh the linked issue so the UI reflects the backend's new
        // status (HITL approval → backend resets issue to in_progress; reject
        // marks it as error; etc.). Fetches the canonical state from the
        // server instead of guessing — earlier code set status='done' on
        // approve, which was semantically wrong for HITL tool approvals.
        const issueId = snapshot?.issue_id || this.currentItem?.issue_id;
        if (issueId) {
          const issueStore = useDocNetIssueStore();
          await issueStore.refreshIssue(issueId);
        }
      } catch (error) {
        // Rollback on failure
        if (hasLocalItem && snapshot) {
          const rollbackIndex = this.items.findIndex((i) => i.id === id);
          if (rollbackIndex !== -1) {
            this.items[rollbackIndex] = snapshot;
          }
        }
        console.error('[DocNet] Failed to act on inbox item:', error);
        throw error;
      }
    },

    async dismissItem(id: string): Promise<void> {
      const index = this.items.findIndex((item) => item.id === id);
      if (index === -1) return;

      // Save snapshot for rollback
      const snapshot = { ...this.items[index] };

      // Optimistic update
      this.items[index] = {
        ...this.items[index],
        status: 'dismissed',
        acted_at: new Date().toISOString(),
      };

      try {
        await inboxService.dismissItem(id);
      } catch (error) {
        // Rollback on failure
        const rollbackIndex = this.items.findIndex((i) => i.id === id);
        if (rollbackIndex !== -1) {
          this.items[rollbackIndex] = snapshot;
        }
        console.error('[DocNet] Failed to dismiss inbox item:', error);
        throw error;
      }
    },

    // ─── Bulk actions ─────────────────────────────────────────────

    toggleSelect(id: string): void {
      if (this.selectedIds.has(id)) {
        this.selectedIds.delete(id);
      } else {
        this.selectedIds.add(id);
      }
    },

    toggleSelectAll(): void {
      const pendingFiltered = this.filteredItems.filter(
        (i) => i.status === 'pending',
      );

      if (this.allFilteredSelected) {
        // Deselect all filtered pending
        for (const item of pendingFiltered) {
          this.selectedIds.delete(item.id);
        }
      } else {
        // Select all filtered pending
        for (const item of pendingFiltered) {
          this.selectedIds.add(item.id);
        }
      }
    },

    clearSelection(): void {
      this.selectedIds.clear();
    },

    async bulkAction(
      action: 'approve' | 'reject' | 'dismiss',
      resolutionNotes?: string,
    ): Promise<void> {
      const ids = [...this.selectedIds];
      if (ids.length === 0) return;

      this.bulkActing = true;

      // Save snapshots for rollback
      const snapshots = new Map<string, DocNetInboxItem>();
      for (const id of ids) {
        const item = this.items.find((i) => i.id === id);
        if (item) snapshots.set(id, { ...item });
      }

      // Optimistic update
      const now = new Date().toISOString();
      const newStatus = action === 'dismiss' ? 'dismissed' : 'resolved';
      for (const id of ids) {
        const index = this.items.findIndex((i) => i.id === id);
        if (index !== -1) {
          this.items[index] = {
            ...this.items[index],
            status: newStatus,
            acted_at: now,
            resolution_notes: resolutionNotes || this.items[index].resolution_notes,
          };
        }
      }

      this.selectedIds.clear();

      try {
        await inboxService.actOnItems(ids, action, resolutionNotes);

        // Refresh linked issues from the server so the UI reflects the
        // canonical backend status. (Previously set status='done'/'error'
        // on the client, which was wrong for HITL tool approvals — backend
        // actually resets the issue to in_progress on approve.)
        const issueStore = useDocNetIssueStore();
        const refreshed = new Set<string>();
        for (const [_id, snapshot] of snapshots) {
          if (snapshot.issue_id && !refreshed.has(snapshot.issue_id)) {
            refreshed.add(snapshot.issue_id);
            await issueStore.refreshIssue(snapshot.issue_id);
          }
        }
      } catch (error) {
        // Rollback all
        for (const [id, snapshot] of snapshots) {
          const index = this.items.findIndex((i) => i.id === id);
          if (index !== -1) {
            this.items[index] = snapshot;
          }
        }
        // Re-select the items that were selected
        for (const id of ids) {
          this.selectedIds.add(id);
        }
        console.error('[DocNet] Failed bulk action:', error);
        throw error;
      } finally {
        this.bulkActing = false;
      }
    },

    // ─── Filters ──────────────────────────────────────────────────

    setFilter(key: keyof DocNetInboxFilters, value: string): void {
      this.filters[key] = value as never;
    },

    clearFilters(): void {
      this.filters = { type: '', status: '' };
    },
  },
});
